import {
  Chip,
  Stack,
  Button,
  Select,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
  LinearProgress,
  SelectChangeEvent
} from "@material-ui/core";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import ListingsMatchRow from "./ListingsMatchRow";
import { addListingToFavorites, removeListingFromFavorites } from "utils/Haim/firestoreUtils";
import { Image } from "components/Image";
import { useNavigate } from "react-router-dom";
import { ListingMatchFavoriteControls } from "./ListingMatchContactListingsBottomOverlay";
import { PageViewStats, ListingDetailViewCount } from "./ListingPageViewIndicator";
import { useListingPageViews } from "hooks/useListingPageViews";
import type { AlgoliaHit } from "instantsearch.js";
import type {
  RealEstateContact,
  ProcessedListingData,
  FavoriteListingsOfContact
} from "../../../@types/sharedSchema";
import ForwardSendIcon from "../../ForwardSendIcon";

type ListingMatchFavoriteListingsProps = {
  contactId?: string;
  showSortBy?: boolean;
  contactData: RealEstateContact | null;
  hideControls?: boolean;
  favoriteListingsOfContact: FavoriteListingsOfContact;
};

const CONTACT_LISTING_SORT_BY = ["latestAddition", "visited"] as const;

type ContactListingSortBy = typeof CONTACT_LISTING_SORT_BY[number];

const CONTACT_LISTING_FILTERS = ["hasNotes", "liked", "disliked", "visited", "notVisited", "sawPage", "didntSawPage"] as const;

type ContactListingFilter = typeof CONTACT_LISTING_FILTERS[number];

const FILTER_PAIRS: [ContactListingFilter, ContactListingFilter][] = [
  ["liked", "disliked"],
  ["visited", "notVisited"],
  ["sawPage", "didntSawPage"]
];

export function ListingMatchFavoriteListings({
  contactId,
  contactData,
  hideControls,
  showSortBy,
  favoriteListingsOfContact
}: ListingMatchFavoriteListingsProps) {
  const [sortBy, setSortBy] = useState<ContactListingSortBy>("latestAddition");
  const [activeFilters, setActiveFilters] = useState<ContactListingFilter[]>([]);

  const [listingsData, setListingsData] = useState<AlgoliaHit<ProcessedListingData>[]>([]);
  const [loading, setLoading] = useState(true);

  const { t: translate } = useTranslation();

  const navigate = useNavigate();

  const { viewData: pageViewData, loading: loadingViews } = useListingPageViews(
    favoriteListingsOfContact?.id
  );

  useEffect(() => {
    const fetchFavoriteListings = async (): Promise<void> => {
      setLoading(true);

      try {
        const favoriteIds = favoriteListingsOfContact?.listingIds ?? [];
        const result = await fetchListingsByIds(favoriteIds);

        // Sort DESCENDING by position in the source `listingIds` array
        // (newest-added is appended last, so a higher index means more
        // recently favorited). Subtracting `a`'s position from `b`'s gives
        // a positive number when b is more recent, which puts b first —
        // i.e. most-recently-added at the top of the list. Doing this
        // explicitly here rather than pre-reversing the input means we
        // don't quietly depend on `fetchListingsByIds` preserving order.
        const positionById = new Map(favoriteIds.map((id, i) => [id, i]));
        const sorted = [...result].sort(
          (a, b) => (positionById.get(b.objectID) ?? -1) - (positionById.get(a.objectID) ?? -1)
        );

        setListingsData(sorted);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };

    void fetchFavoriteListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event: SelectChangeEvent<ContactListingSortBy>) => {
    setSortBy(event.target.value as ContactListingSortBy);
  };

  const toggleFilter = (filter: ContactListingFilter) => {
    setActiveFilters((prev) => {
      if (prev.includes(filter)) return prev.filter((f) => f !== filter);

      const pair = FILTER_PAIRS.find((p) => p.includes(filter));
      const opposite = pair?.find((f) => f !== filter);
      const withoutOpposite = opposite ? prev.filter((f) => f !== opposite) : prev;

      return [...withoutOpposite, filter];
    });
  };

  const handleSendListing = (): void => {
    navigate(`/dashboard/listingsMatch?cn=${contactId}`);
  };

  const favoritePath = favoriteListingsOfContact?.path;

  const onAddToFavorite = useCallback(
    (hit: AlgoliaHit<ProcessedListingData>) => addListingToFavorites(favoritePath, hit.objectID),
    [favoritePath]
  );
  const onRemoveFromFavorite = useCallback(
    (hit: AlgoliaHit<ProcessedListingData>) => removeListingFromFavorites(favoritePath, hit.objectID),
    [favoritePath]
  );

  const showSortDropdown = showSortBy || !hideControls;
  const shouldSortListingsByVisited = showSortDropdown && sortBy === "visited";

  const parsedListingsData = useMemo(() => {
    let result = listingsData;

    if (activeFilters.length > 0) {
      result = result.filter((hit) => {
        const id = hit.objectID;
        const hasNotes = !!favoriteListingsOfContact.additionalDetails?.[id]?.listingNotes;
        const isLiked = favoriteListingsOfContact.listingIdsUserLiked?.includes(id);
        const isDisliked = favoriteListingsOfContact.listingIdsUserDisliked?.includes(id);
        const isVisited = favoriteListingsOfContact.listingIdsUserVisited?.includes(id);
        const sawPage = (pageViewData?.listingDetailViewCounts?.[id] ?? 0) > 0;

        return activeFilters.every((filter) => {
          switch (filter) {
            case "hasNotes": return hasNotes;
            case "liked": return isLiked;
            case "disliked": return isDisliked;
            case "visited": return isVisited;
            case "notVisited": return !isVisited;
            case "sawPage": return sawPage;
            case "didntSawPage": return !sawPage;
            default: return true;
          }
        });
      });
    }

    if (shouldSortListingsByVisited) {
      result = result.slice().sort((a, b) => {
        const aVisited = favoriteListingsOfContact.listingIdsUserVisited?.includes(a.objectID);
        const bVisited = favoriteListingsOfContact.listingIdsUserVisited?.includes(b.objectID);

        if (aVisited && !bVisited) return -1;
        if (!aVisited && bVisited) return 1;
        return 0;
      });
    }

    return result;
  }, [listingsData, activeFilters, shouldSortListingsByVisited, favoriteListingsOfContact, pageViewData]);

  if (loading) return <LinearProgress />;

  if (!listingsData.length) {
    return (
      <Stack alignItems="center" gap={2}>
        <Image sx={{ maxHeight: 256 }} src="/static/illustrations/illustration_invite.png" />
        <Typography variant="body1">{translate("listingsMatch.noListingMatchesFound")}</Typography>
        {contactId && (
          <Button variant="contained" endIcon={<ForwardSendIcon />} onClick={handleSendListing}>
            {translate("contactsPage.control.sendListing")}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {!hideControls && (
        <ListingMatchFavoriteControls
          minisiteId={favoriteListingsOfContact?.id}
          disabled={!favoriteListingsOfContact?.listingIds?.length}
          phoneNumber={favoriteListingsOfContact?.contactId}
        >
          {!loadingViews && <PageViewStats viewData={pageViewData} />}
          <FormControl
            sx={{
              width: "100%",
              maxWidth: {
                xs: "100%",
                sm: 192
              }
            }}
          >
            <InputLabel id="sortBy">{translate("listingsMatch.sortBy.title")}</InputLabel>
            <Select
              id="sortBy"
              labelId="sortBy"
              value={sortBy}
              label={translate("listingsMatch.sortBy.title")}
              onChange={handleChange}
            >
              {CONTACT_LISTING_SORT_BY.map((option) => (
                <MenuItem key={option} value={option}>
                  {translate(`listingsMatch.sortBy.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ListingMatchFavoriteControls>
      )}
      {hideControls && showSortBy && (
        <Stack gap={1.5} pt={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <FormControl size="small" sx={{ minWidth: 160, maxWidth: 192 }}>
              <InputLabel id="sortBy">{translate("listingsMatch.sortBy.title")}</InputLabel>
              <Select
                id="sortBy"
                labelId="sortBy"
                value={sortBy}
                label={translate("listingsMatch.sortBy.title")}
                onChange={handleChange}
              >
                {CONTACT_LISTING_SORT_BY.map((option) => (
                  <MenuItem key={option} value={option}>
                    {translate(`listingsMatch.sortBy.${option}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {contactId && (
              <Button variant="contained" endIcon={<ForwardSendIcon />} onClick={handleSendListing}>
                {translate("contactsPage.control.sendMoreListings")}
              </Button>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="caption" color="text.secondary">
              {translate("listingsMatch.filterBy.title")}:
            </Typography>
            <Chip
              size="small"
              label={translate("listingsMatch.filterBy.hasNotes")}
              variant={activeFilters.includes("hasNotes") ? "filled" : "outlined"}
              color={activeFilters.includes("hasNotes") ? "primary" : "default"}
              onClick={() => toggleFilter("hasNotes")}
            />
            {FILTER_PAIRS.map(([a, b]) => (
              <Stack key={a} direction="row" gap={0.5}>
                <Chip
                  size="small"
                  label={translate(`listingsMatch.filterBy.${a}`)}
                  variant={activeFilters.includes(a) ? "filled" : "outlined"}
                  color={activeFilters.includes(a) ? "primary" : "default"}
                  onClick={() => toggleFilter(a)}
                />
                <Chip
                  size="small"
                  label={translate(`listingsMatch.filterBy.${b}`)}
                  variant={activeFilters.includes(b) ? "filled" : "outlined"}
                  color={activeFilters.includes(b) ? "primary" : "default"}
                  onClick={() => toggleFilter(b)}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
      {hideControls && !showSortBy && contactId && (
        <Stack alignItems="center" gap={2}>
          <Button variant="contained" endIcon={<ForwardSendIcon />} onClick={handleSendListing}>
            {translate("contactsPage.control.sendMoreListings")}
          </Button>
        </Stack>
      )}
      {!hideControls && (
        <Stack alignItems="center" gap={2}>
          {contactId && (
            <Button variant="contained" endIcon={<ForwardSendIcon />} onClick={handleSendListing}>
              {translate("contactsPage.control.sendMoreListings")}
            </Button>
          )}
        </Stack>
      )}
      {activeFilters.length > 0 && parsedListingsData.length === 0 && (
        <Stack alignItems="center" justifyContent="center" flex={1} py={4}>
          <Typography variant="body1" color="text.secondary">
            {translate("listingsMatch.filterBy.noResults")}
          </Typography>
        </Stack>
      )}
      {parsedListingsData.map((hit, index) => {
        const isFavorite = favoriteListingsOfContact?.listingIds?.includes(hit.objectID);
        const isImagesHidden = favoriteListingsOfContact?.listingIdsWithPicturesHidden?.includes(
          hit.objectID
        );

        return (
          <Stack key={index} spacing={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <ListingDetailViewCount listingId={hit.objectID} viewData={pageViewData} />
            </Stack>
            <ListingsMatchRow
              key={hit.objectID}
              listing={hit}
              showFavoriteButton={true}
              isFavorite={isFavorite}
              imagesHidden={isImagesHidden}
              pickedContact={contactData}
              favoriteListingsOfContact={favoriteListingsOfContact}
              onAddToFavorite={onAddToFavorite}
              onRemoveFromFavorite={onRemoveFromFavorite}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}
